// ✅ FULL UPDATED CODE (as requested)
// Adds:
// 1) Show table data ONLY after user selects >=1 filter AND clicks Apply Filter
// 2) Apply Filter + Clear buttons
// 3) Bulk selection + Set Bulk Active / Set Bulk Inactive (updates only selected visible rows)
// 4) Restores Download Sample Excel (sample data + instructions)
// Keeps:
// - Your theme tokens and styling
// - Existing CRUD (add/update/delete) + bulk upload
// Notes:
// - Uses row.id as the unique key (same as your edit/update/delete logic)
// - Filters used here: Regulation, SemesterCode, SubjectNameCode (same as your visible filter UI)

import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Autocomplete,
  CircularProgress,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import { API_URL, API_UPLOAD_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const CodeSubjectDetails = () => {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const tableWrapSx = { ...surfaceSx, mt: 2, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
    verticalAlign: "top",
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    SubjectCode: "",
    SubjectName: "",
    SubjectNameCode: "",
    Semester: "",
    Regulation: "",
    SubjectNameAlias1: "",
    SubjectNameAlias2: "",
    Credits: "",
    Capacity: "",
    IsActiveSubject: 0,
  });
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const fileInputRef = useRef();
  const token = localStorage.getItem("auth");
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  // ✅ NEW: selection state for bulk
  const [selectedRows, setSelectedRows] = useState([]); // array of row.id

  const fetchData = async () => {
    const res = await axios.get(`${API_URL}/api/CodeSubjectDetails`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setData(res.data || []);
  };

  // ✅ Restore: Download sample excel
  const downloadSampleExcel = () => {
    const cc = campusCode || "CAMPUS01";

    const headers = [
      "CampusCode",
      "Regulation",
      "SubjectCode",
      "SubjectName",
      "SubjectNameCode",
      "Semester",
      "SubjectNameAlias1",
      "SubjectNameAlias2",
      "Credits",
      "Capacity",
      "IsActiveSubject",
    ];

    const sampleRows = [
      {
        CampusCode: cc,
        Regulation: "R20",
        SubjectCode: "CS101",
        SubjectName: "Programming Fundamentals",
        SubjectNameCode: "PF",
        Semester: 1,
        SubjectNameAlias1: "Intro to Programming",
        SubjectNameAlias2: "Python Basics",
        Credits: 3,
        Capacity: 60,
        IsActiveSubject: 1,
      },
      {
        CampusCode: cc,
        Regulation: "R21",
        SubjectCode: "MA102",
        SubjectName: "Engineering Mathematics",
        SubjectNameCode: "EM",
        Semester: 1,
        SubjectNameAlias1: "Math-I",
        SubjectNameAlias2: "Calculus",
        Credits: 4,
        Capacity: 60,
        IsActiveSubject: 1,
      },
      {
        CampusCode: cc,
        Regulation: "R22",
        SubjectCode: "EC201",
        SubjectName: "Digital Electronics",
        SubjectNameCode: "DE",
        Semester: 3,
        SubjectNameAlias1: "Logic Design",
        SubjectNameAlias2: "",
        Credits: 3,
        Capacity: 50,
        IsActiveSubject: 0,
      },
    ];

    const normalized = sampleRows.map((r) => {
      const obj = {};
      headers.forEach((h) => (obj[h] = r[h] ?? ""));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(normalized, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CodeSubjectDetails");

    const instructions = [
      ["Field", "Notes / Example"],
      ["CampusCode", "Auto-filled from login"],
      ["Regulation", "Example: R20 / R21 / R22"],
      ["SubjectCode", "Unique subject code (CS101, MA102)"],
      ["SubjectName", "Full subject name"],
      ["SubjectNameCode", "Short code (PF, EM)"],
      ["Semester", "Numeric semester (1,2,3...)"],
      ["SubjectNameAlias1", "Optional alias"],
      ["SubjectNameAlias2", "Optional alias"],
      ["Credits", "Numeric credits (3,4)"],
      ["Capacity", "Max students (e.g., 60)"],
      ["IsActiveSubject", "1 = Active, 0 = Inactive"],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 22 }, { wch: 70 }];
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `CodeSubjectDetails_Sample_${cc}.xlsx`);
  };

  // Decode CampusCode from JWT
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow((r) => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Filters =====
  const [filters, setFilters] = useState({
    Regulation: [],
    SemesterCode: [],
    SubjectNameCode: [],
  });

  const [options, setOptions] = useState({
    Regulation: [],
    SemesterCode: [],
    SubjectNameCode: [],
  });

  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions({
          Regulation: data?.Regulation || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
          SubjectNameCode: data?.SubjectNameCode || [],
        });
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  // ✅ NEW: appliedFilters -> data is shown ONLY after Apply
  const [appliedFilters, setAppliedFilters] = useState(null);

  const hasAnyFilterSelected = useMemo(() => {
    return Object.values(filters).some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [filters]);

  const hasAnyAppliedFilter = useMemo(() => {
    if (!appliedFilters) return false;
    return Object.values(appliedFilters).some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [appliedFilters]);

  const handleApplyFilters = () => {
    if (!hasAnyFilterSelected) {
      setToast({ open: true, type: "warning", msg: "Select at least one filter to view data." });
      setAppliedFilters(null);
      setSelectedRows([]);
      return;
    }
    setAppliedFilters({ ...filters });
    setSelectedRows([]);
  };

  const handleClearFilters = () => {
    setFilters({ Regulation: [], SemesterCode: [], SubjectNameCode: [] });
    setAppliedFilters(null); // hides data again
    setSelectedRows([]);
  };

  // helpers: normalize + match
  const norm = (v) => String(v ?? "").trim().toLowerCase();
  const inFilter = (selected = [], value) => !selected?.length || selected.some((s) => norm(s) === norm(value));

  // filtered rows (based on appliedFilters only)
  const appliedFilteredRows = useMemo(() => {
    if (!appliedFilters) return [];
    return (data || []).filter((r) => {
      const okReg = inFilter(appliedFilters.Regulation, r.Regulation);
      const okSem = inFilter(appliedFilters.SemesterCode, String(r.Semester));
      const okSub = inFilter(appliedFilters.SubjectNameCode, r.SubjectNameCode);
      return okReg && okSem && okSub;
    });
  }, [data, appliedFilters]);

  // keep selection in sync with visible rows
  useEffect(() => {
    const visibleIds = new Set(appliedFilteredRows.map((r) => r.id));
    setSelectedRows((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [appliedFilteredRows]);

  // Initial data load (we load data, but show nothing until filters applied)
  useEffect(() => {
    if (!campusCode) return;
    fetchData().catch(() => setError("Failed to fetch data"));
  }, [campusCode]);

  // Unified input handler
  const handleInputChange = (e, id = null) => {
    const { name, value, checked } = e.target;
    const v = name === "IsActiveSubject" ? (checked ? 1 : 0) : value;
    if (id == null) setNewRow((r) => ({ ...r, [name]: v }));
    else setData((d) => d.map((r) => (r.id === id ? { ...r, [name]: v } : r)));
  };

  const handleNewRowActiveToggle = (e) =>
    setNewRow((r) => ({ ...r, IsActiveSubject: e.target.checked ? 1 : 0 }));

  // Add
  const handleAddRow = async () => {
    const prev = data;
    try {
      await axios.post(`${API_URL}/api/CodeSubjectDetails`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setError("");
      await fetchData();
      setNewRow({
        CampusCode: campusCode,
        Regulation: "",
        SubjectCode: "",
        SubjectName: "",
        SubjectNameCode: "",
        Semester: "",
        SubjectNameAlias1: "",
        SubjectNameAlias2: "",
        Credits: "",
        Capacity: "",
        IsActiveSubject: 0,
      });
    } catch (err) {
      setError(err.response?.status === 409 ? err.response.data.error : "Failed to add row");
      setData(prev);
    }
  };

  // Update
  const handleUpdateRow = async (id) => {
    const prev = data;
    const updated = data.find((r) => r.id === id);
    try {
      await axios.put(`${API_URL}/api/CodeSubjectDetails/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setEditRowId(null);
      setError("");
      await fetchData();
    } catch (err) {
      setError(err.response?.status === 409 ? err.response.data.error : "Failed to update row");
      setData(prev);
    }
  };

  // Delete
  const handleDeleteRow = async (id) => {
    const prev = data;
    try {
      await axios.delete(`${API_URL}/api/CodeSubjectDetails/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setError("");
      await fetchData();
    } catch (err) {
      setError(err.response?.status === 409 ? err.response.data.error : "Failed to delete row");
      setData(prev);
    }
  };

  // Bulk upload
  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async (e) => {
    const prev = data;
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    try {
      await axios.post(`${API_URL}/api/UploadCodeSubjectDetails`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      await fetchData();
      setSuccess(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
      setData(prev);
    } finally {
      e.target.value = "";
    }
  };

  // ✅ Bulk selection helpers (visible rows only)
  const allSelected =
    appliedFilteredRows.length > 0 && appliedFilteredRows.every((r) => selectedRows.includes(r.id));
  const isRowSelected = (id) => selectedRows.includes(id);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedRows(appliedFilteredRows.map((r) => r.id));
    else setSelectedRows([]);
  };

  const handleSelectRow = (id) => (e) => {
    if (e.target.checked) setSelectedRows((prev) => [...prev, id]);
    else setSelectedRows((prev) => prev.filter((x) => x !== id));
  };

  // ✅ Bulk Active/Inactive
  const handleBulkActiveUpdate = async (makeActive) => {
    if (!selectedRows.length) return;

    try {
      const promises = selectedRows.map((id) => {
        const row = data.find((r) => r.id === id);
        if (!row) return Promise.resolve();
        return axios.put(
          `${API_URL}/api/CodeSubjectDetails/${id}`,
          { ...row, IsActiveSubject: makeActive ? 1 : 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      });

      await Promise.all(promises);
      setToast({
        open: true,
        type: "success",
        msg: `Updated ${selectedRows.length} row(s)`,
      });
      setSelectedRows([]);
      await fetchData();
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed bulk update." });
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Subject Details
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter, add, and maintain subject master data
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              disabled={!hasAnyAppliedFilter || selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(true)}
            >
              Set Bulk Active
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={!hasAnyAppliedFilter || selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(false)}
            >
              Set Bulk Inactive
            </Button>

            <Divider flexItem orientation="vertical" sx={{ borderColor: "rgba(255,255,255,0.35)" }} />

            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
              Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Messages */}
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!!success && (
            <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                Operation successful!
              </Alert>
            </Snackbar>
          )}

          <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
            <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))} sx={{ borderRadius: 2 }}>
              {toast.msg}
            </Alert>
          </Snackbar>

          {/* ✅ Filters section */}
          <Paper variant="outlined" sx={{ ...sectionSx, mt: 0 }}>
            <Box sx={{ pl: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Filters</Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={handleApplyFilters}
                    disabled={!hasAnyFilterSelected}
                    title={!hasAnyFilterSelected ? "Select at least one filter" : "Apply filters"}
                  >
                    Apply Filter
                  </Button>
                  <Button
                    variant="outlined"
                    sx={outlinedBtnSx}
                    onClick={handleClearFilters}
                    disabled={!hasAnyFilterSelected && !appliedFilters}
                  >
                    Clear
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(8, 1fr)" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.Regulation}
                  value={filters.Regulation}
                  onChange={(_, value) => setFilters((p) => ({ ...p, Regulation: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Regulation(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SubjectNameCode}
                  value={filters.SubjectNameCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubjectNameCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubjectNameCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {!hasAnyAppliedFilter && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(25,118,210,0.20)",
                    backgroundColor: "rgba(25,118,210,0.04)",
                  }}
                >
                  Select at least one filter and click <b>Apply Filter</b> to view data.
                </Alert>
              )}
            </Box>
          </Paper>

          {/* ✅ Table */}
      {/* OUTER – keeps rounded corners & spacing */}
      <Paper elevation={0} sx={tableWrapSx}>

        {/* INNER – ONLY for horizontal scroll */}
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          <TableContainer component="div">
            <Table size="small" sx={{ width: "100%" }}>
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: 50 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 260 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 110 }} />
              </colgroup>

              <TableHead>
                <TableRow sx={theadRowSx}>
                  <TableCell>#</TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedRows.length > 0 && !allSelected}
                      onChange={handleSelectAll}
                      disabled={!hasAnyAppliedFilter || appliedFilteredRows.length === 0}
                      sx={{
                        ...checkboxSx,
                        color: "rgba(255,255,255,0.95)",
                        "&.Mui-checked": { color: "#fff" },
                      }}
                    />
                  </TableCell>

                  {[
                    "Campus",
                    "Regulation",
                    "Subject",
                    "Subject Name",
                    "Name Code",
                    "Semester",
                    "Alias1",
                    "Alias2",
                    "Credits",
                    "Capacity",
                    "Active",
                    "Actions",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {!hasAnyAppliedFilter ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                      Apply at least one filter to view subject details.
                    </TableCell>
                  </TableRow>
                ) : appliedFilteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ py: 3, textAlign: "center", opacity: 0.7 }}>
                      No subject details found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  appliedFilteredRows.map((row, idx) =>
                    editRowId === row.id ? (
                      <TableRow key={row.id} sx={zebraRowSx(idx)}>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell padding="checkbox">
                          <Checkbox checked={isRowSelected(row.id)} onChange={handleSelectRow(row.id)} sx={checkboxSx} />
                        </TableCell>

                        <TableCell>{row.CampusCode}</TableCell>

                        {[
                          { name: "Regulation" },
                          { name: "SubjectCode" },
                          { name: "SubjectName" },
                          { name: "SubjectNameCode" },
                          { name: "Semester" },
                          { name: "SubjectNameAlias1" },
                          { name: "SubjectNameAlias2" },
                          { name: "Credits" },
                          { name: "Capacity" },
                        ].map((f) => (
                          <TableCell key={f.name}>
                            <TextField
                              name={f.name}
                              value={row[f.name]}
                              onChange={(e) => handleInputChange(e, row.id)}
                              fullWidth
                              size="small"
                              sx={inputSx}
                            />
                          </TableCell>
                        ))}

                        <TableCell>
                          <Checkbox
                            name="IsActiveSubject"
                            checked={!!row.IsActiveSubject}
                            onChange={(e) => handleInputChange(e, row.id)}
                            sx={checkboxSx}
                          />
                        </TableCell>

                        <TableCell>
                          <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton sx={iconBtnSx} onClick={() => setEditRowId(null)} title="Cancel">
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={row.id} sx={zebraRowSx(idx)}>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell padding="checkbox">
                          <Checkbox checked={isRowSelected(row.id)} onChange={handleSelectRow(row.id)} sx={checkboxSx} />
                        </TableCell>

                        <TableCell>{row.CampusCode}</TableCell>
                        <TableCell>{row.Regulation}</TableCell>
                        <TableCell>{row.SubjectCode}</TableCell>
                        <TableCell>{row.SubjectName}</TableCell>
                        <TableCell>{row.SubjectNameCode}</TableCell>
                        <TableCell>{row.Semester}</TableCell>
                        <TableCell>{row.SubjectNameAlias1}</TableCell>
                        <TableCell>{row.SubjectNameAlias2}</TableCell>
                        <TableCell>{row.Credits}</TableCell>
                        <TableCell>{row.Capacity}</TableCell>
                        <TableCell>
                          <Checkbox checked={!!row.IsActiveSubject} readOnly sx={checkboxSx} />
                        </TableCell>
                        <TableCell>
                          <IconButton sx={iconBtnSx} onClick={() => setEditRowId(row.id)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  )
                )}

                {/* new-row form */}
                <TableRow sx={zebraRowSx(hasAnyAppliedFilter ? appliedFilteredRows.length : 0)}>
                  <TableCell />
                  <TableCell padding="checkbox">
                    <Checkbox disabled sx={checkboxSx} />
                  </TableCell>

                  <TableCell>
                    <TextField value={campusCode} disabled fullWidth size="small" sx={inputSx} />
                  </TableCell>

                  {[
                    { name: "Regulation", placeholder: "Regulation" },
                    { name: "SubjectCode", placeholder: "Subject" },
                    { name: "SubjectName", placeholder: "Subject Name" },
                    { name: "SubjectNameCode", placeholder: "Name Code" },
                    { name: "Semester", placeholder: "Semester" },
                    { name: "SubjectNameAlias1", placeholder: "Alias1" },
                    { name: "SubjectNameAlias2", placeholder: "Alias2" },
                    { name: "Credits", placeholder: "Credits" },
                    { name: "Capacity", placeholder: "Capacity" },
                  ].map((f) => (
                    <TableCell key={f.name}>
                      <TextField
                        name={f.name}
                        value={newRow[f.name]}
                        onChange={handleInputChange}
                        placeholder={f.placeholder}
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </TableCell>
                  ))}

                  <TableCell>
                    <Checkbox
                      name="IsActiveSubject"
                      checked={!!newRow.IsActiveSubject}
                      onChange={handleNewRowActiveToggle}
                      sx={checkboxSx}
                    />
                  </TableCell>

                  <TableCell>
                    <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default CodeSubjectDetails;
